import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { walletStatusList, IWallet } from '@/features/wallets/store/models/wallet.model';

import { useFavorites } from '@/hooks/useFavorites';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

export function useUserWalletOverview(wallets: IWallet[]) {
  const { t } = useTranslation();
  const { favorites } = useFavorites();

  const totalRows = wallets?.[0]?.totalRows;

  const navigate = useNavigate();
  const handleNavigate = useCallback(
    (wallet?: IWallet) => {
      if (wallets?.length) {
        const { userId, walletId } = wallet || wallets[0];
        navigate(DashboardHelpers.generateProfilePath(userId, { walletId }));
      }
    },
    [wallets, navigate],
  );

  const { currentBalance, authorizedBalance, numberDebtor } = wallets.reduce(
    (acc, currentWallet) => {
      const walletSolde = currentWallet?.solde ?? 0;
      const walletAuthorizedBalance = currentWallet?.authorizedBalance ?? 0;

      if (walletSolde < 0) {
        acc.numberDebtor += 1;
      }

      acc.currentBalance += walletSolde;
      acc.authorizedBalance += walletAuthorizedBalance;
      return acc;
    },
    { currentBalance: 0, authorizedBalance: 0, numberDebtor: 0 },
  );

  const sortedWallets = [...wallets].sort((a, b) => {
    const isAFavorite = favorites.some((favorite) => favorite.walletId === a.walletId);
    const isBFavorite = favorites.some((favorite) => favorite.walletId === b.walletId);
    if (isAFavorite && !isBFavorite) return -1;
    if (!isAFavorite && isBFavorite) return 1;

    if (b.solde !== a.solde) {
      return b.solde - a.solde;
    }

    const isAValidated = a?.walletStatus === walletStatusList.VALIDATED;
    const isBValidated = b?.walletStatus === walletStatusList.VALIDATED;
    if (isAValidated && !isBValidated) return -1;
    if (!isAValidated && isBValidated) return 1;

    try {
      const dateA = new Date(a?.createdDate);
      const dateB = new Date(b?.createdDate);
      if (isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) return 1;
      if (!isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return -1;
      return dateB.getTime() - dateA.getTime();
    } catch (e) {
      console.error(e);
      return 0;
    }
  });

  const hasAllWallets = sortedWallets?.length <= 2;

  const displayedWallets = hasAllWallets ? sortedWallets : sortedWallets.splice(0, 2);
  const overviews = [
    {
      label: t('pages.currentProfiles.profile.overview.globalBalance'),
      value: currentBalance,
    },
    {
      label: t('pages.currentProfiles.profile.overview.globalAuthorizedBalance'),
      value: authorizedBalance,
    },
    {
      label: t('pages.currentProfiles.profile.overview.globalAuthorizations'),
      value: currentBalance - authorizedBalance,
    },
    ...(numberDebtor > 0
      ? [
          {
            label: t('pages.currentProfiles.profile.overview.debtorWallets'),
            value: numberDebtor,
            hasWarning: true,
          },
        ]
      : []),
  ];

  return {
    overviews,
    displayedWallets,
    hasAllWallets,
    handleNavigate,
    totalRows: totalRows || wallets?.length,
  };
}
